import {useId, useRef, type Key, type MouseEvent, type ReactNode} from 'react';
import {NavLink, useNavigate} from 'react-router';
import {useTranslation} from 'react-i18next';
import {Card, Form, Spinner, Button, Table} from '@goproblems/ui';
import {Layout} from '@components/Layout';
import {SiteTitle} from '@components/SiteTitle';
import {DashboardTabbedCard} from '@components/DashboardTabbedCard';
import {DashboardTabs} from '@components/DashboardTabs';
import {CustomPagination} from '@components/CustomPagination';
import {UserIdentity} from '@components/common/UserIdentity';
import {useAuthenticated} from '@utils';
import type {UserMinimal} from '@types';
import '../../styles/leaderboards.scss';

export const LeaderboardPage = ({
    title,
    summary,
    tabs,
    action,
    toolbar,
    children,
}: {
    title: string;
    summary?: ReactNode;
    tabs?: ReactNode;
    action?: ReactNode;
    toolbar?: ReactNode;
    children: ReactNode;
}) => (
    <Layout containerClassName="dashboard-page leaderboard-page">
        <SiteTitle title={title} />
        <div className="dashboard-content">
            <DashboardTabbedCard
                title={title}
                summary={summary}
                tabs={tabs}
                action={action}>
                {toolbar && (
                    <div className="leaderboard-toolbar">{toolbar}</div>
                )}
                <Card.Body className="leaderboard-body">{children}</Card.Body>
            </DashboardTabbedCard>
        </div>
    </Layout>
);

export const LeaderboardNavigation = () => {
    const {t} = useTranslation();
    return (
        <nav
            className="leaderboard-navigation"
            aria-label={t('leaderboard_ui.navigation', 'Leaderboards')}>
            <NavLink to="/leaderboard" end>
                {t('leaderboard_ui.overall', 'Overall')}
            </NavLink>
            <NavLink to="/daily-leaderboard" end>
                {t('leaderboard_ui.daily', 'Daily')}
            </NavLink>
        </nav>
    );
};

export const LeaderboardScope = ({
    value,
    onChange,
}: {
    value: string;
    onChange: (value: string) => void;
}) => {
    const {isAuthenticated} = useAuthenticated();
    const {t} = useTranslation();
    const scopes = isAuthenticated
        ? ['personal', 'friends', 'global']
        : ['global'];
    return (
        <DashboardTabs
            label={t('leaderboard_ui.scope', 'Leaderboard scope')}
            activeKey={value}
            onSelect={onChange}
            items={scopes.map(key => ({
                key,
                label: t(`rush_leaderboard.sections.${key}`),
            }))}
        />
    );
};

export const LeaderboardSelect = ({
    label,
    value,
    onChange,
    options,
}: {
    label: string;
    value: string | number;
    onChange: (value: string) => void;
    options: {value: string | number; label: string}[];
}) => {
    const id = useId();
    return (
        <div className="leaderboard-filter">
            <label htmlFor={id}>{label}</label>
            <Form.Select
                id={id}
                value={value}
                onChange={event => onChange(event.target.value)}>
                {options.map(option => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </Form.Select>
        </div>
    );
};

export const LeaderboardPeriod = ({
    value,
    onChange,
}: {
    value: string;
    onChange: (value: string) => void;
}) => {
    const {t} = useTranslation();
    return (
        <LeaderboardSelect
            label={t('leaderboard_ui.period', 'Period')}
            value={value}
            onChange={onChange}
            options={['all_time', 'year', 'month', 'week', 'day'].map(
                value => ({
                    value,
                    label: t(`rush_leaderboard.options.${value}`),
                })
            )}
        />
    );
};

export const LeaderboardPosition = ({position}: {position: number}) => (
    <span
        className={`leaderboard-position${position <= 3 ? ` leaderboard-position-${position}` : ''}`}>
        {position}
    </span>
);

const LeaderboardUser = ({
    user,
    anonymous = false,
}: {
    user?: UserMinimal | null;
    anonymous?: boolean;
}) => {
    const {t} = useTranslation();
    return (
        <UserIdentity
            user={user}
            anonymous={anonymous}
            anonymousLabel={t('daily_leaderboard.anonymous_user')}
            avatarSize={36}
            showRank
            className="leaderboard-user"
        />
    );
};

export type LeaderboardColumn = {
    key: string;
    label: string;
    mobileLabel?: string;
    sortable?: boolean;
    align?: 'left' | 'right';
};
export type LeaderboardRow = {
    key: Key;
    user?: UserMinimal | null;
    anonymous?: boolean;
    position?: number;
    resultTo?: string;
    values: Record<string, ReactNode>;
};

const LeaderboardMobileStats = ({
    columns,
    row,
    className = 'leaderboard-entry-details',
}: {
    columns: LeaderboardColumn[];
    row: LeaderboardRow;
    className?: string;
}) =>
    columns.length > 0 && (
        <dl className={className}>
            {columns.map(column => (
                <div key={column.key}>
                    <dt>{column.mobileLabel ?? column.label}</dt>
                    <dd>{row.values[column.key] ?? '—'}</dd>
                </div>
            ))}
        </dl>
    );

export const LeaderboardTable = ({
    columns,
    rows,
    primaryColumn,
    mobileSummaryColumns,
    sortBy,
    sortDirection = 'descending',
    onSort,
    isLoading,
    isError,
    onRetry,
    emptyMessage,
    showPositions = true,
}: {
    columns: LeaderboardColumn[];
    rows: LeaderboardRow[];
    primaryColumn: string;
    mobileSummaryColumns?: string[];
    sortBy?: string;
    sortDirection?: 'ascending' | 'descending';
    onSort?: (key: string) => void;
    isLoading?: boolean;
    isError?: boolean;
    onRetry?: () => void;
    emptyMessage?: string;
    showPositions?: boolean;
}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {user: authUser} = useAuthenticated();
    const openResult = (event: MouseEvent<HTMLElement>, to?: string) => {
        if (
            !to ||
            (event.target instanceof Element &&
                event.target.closest(
                    'a, button, input, select, textarea, summary, details, [role="button"]'
                ))
        ) {
            return;
        }
        navigate(to);
    };
    const primary =
        columns.find(column => column.key === primaryColumn) ?? columns[0];
    const secondaryColumns = columns.filter(
        column => column.key !== primary?.key
    );
    const summaryColumns = mobileSummaryColumns
        ? secondaryColumns.filter(column =>
              mobileSummaryColumns.includes(column.key)
          )
        : secondaryColumns;
    const additionalColumns = mobileSummaryColumns
        ? secondaryColumns.filter(
              column => !mobileSummaryColumns.includes(column.key)
          )
        : [];
    if (isLoading)
        return (
            <div className="leaderboard-state" role="status">
                <Spinner />
                <span>{t('attempts.loading')}</span>
            </div>
        );
    if (isError)
        return (
            <div className="leaderboard-state" role="alert">
                <p>
                    {t(
                        'leaderboard_ui.load_error',
                        'Unable to load the leaderboard.'
                    )}
                </p>
                {onRetry && (
                    <Button variant="outline-secondary" onClick={onRetry}>
                        {t('leaderboard_ui.retry', 'Try again')}
                    </Button>
                )}
            </div>
        );
    if (!rows.length)
        return (
            <div className="leaderboard-state">
                <i className="fa-solid fa-ranking-star" aria-hidden="true" />
                <p>
                    {emptyMessage ??
                        t(
                            'leaderboard_ui.empty',
                            'No results for these filters yet.'
                        )}
                </p>
            </div>
        );
    const isCurrent = (row: LeaderboardRow) =>
        !row.anonymous && !!row.user && row.user.id === authUser?.id;
    return (
        <>
            <div className="dashboard-table-view">
                <Table
                    responsive
                    className={`leaderboard-table${columns.length > 5 ? ' leaderboard-table-wide' : ''}`}>
                    <thead>
                        <tr>
                            {showPositions && (
                                <th className="leaderboard-position-cell">
                                    <span
                                        aria-label={t(
                                            'leaderboard_ui.position',
                                            'Position'
                                        )}>
                                        #
                                    </span>
                                </th>
                            )}
                            <th className="leaderboard-user-cell">
                                {t('leaderboard.user')}
                            </th>
                            {columns.map(column => (
                                <th
                                    key={column.key}
                                    className={
                                        column.align === 'left'
                                            ? 'leaderboard-cell-text'
                                            : 'leaderboard-cell-number'
                                    }
                                    aria-sort={
                                        column.sortable
                                            ? sortBy === column.key
                                                ? sortDirection
                                                : 'none'
                                            : undefined
                                    }>
                                    {column.sortable && onSort ? (
                                        <button
                                            type="button"
                                            className="leaderboard-sort-heading"
                                            onClick={() => onSort(column.key)}>
                                            {column.label}
                                            <i
                                                aria-hidden="true"
                                                className={`fa-solid ${sortBy === column.key ? (sortDirection === 'ascending' ? 'fa-arrow-up' : 'fa-arrow-down') : 'fa-sort'}`}
                                            />
                                        </button>
                                    ) : (
                                        column.label
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr
                                key={row.key}
                                className={[
                                    isCurrent(row) && 'leaderboard-current-user',
                                    row.resultTo && 'leaderboard-result-row',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                onClick={
                                    row.resultTo
                                        ? event =>
                                              openResult(event, row.resultTo)
                                        : undefined
                                }>
                                {showPositions && (
                                    <td className="leaderboard-position-cell">
                                        {row.position !== undefined && (
                                            <LeaderboardPosition
                                                position={row.position}
                                            />
                                        )}
                                    </td>
                                )}
                                <td>
                                    <LeaderboardUser
                                        user={row.user}
                                        anonymous={row.anonymous}
                                    />
                                </td>
                                {columns.map(column => (
                                    <td
                                        key={column.key}
                                        className={`${column.align === 'left' ? 'leaderboard-cell-text' : 'leaderboard-cell-number'}${column.key === primary.key ? ' leaderboard-primary-value' : ''}`}>
                                        {row.values[column.key] ?? '—'}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
            <div className="leaderboard-mobile-list">
                {rows.map(row => (
                    <article
                        key={row.key}
                        className={`leaderboard-entry${isCurrent(row) ? ' leaderboard-current-user' : ''}${row.resultTo ? ' leaderboard-result-row' : ''}`}
                        onClick={
                            row.resultTo
                                ? event => openResult(event, row.resultTo)
                                : undefined
                        }>
                        <div className="leaderboard-entry-heading">
                            {showPositions && row.position !== undefined && (
                                <LeaderboardPosition position={row.position} />
                            )}
                            <LeaderboardUser
                                user={row.user}
                                anonymous={row.anonymous}
                            />
                            <div className="leaderboard-entry-score">
                                <span>
                                    {primary.mobileLabel ?? primary.label}
                                </span>
                                <strong>
                                    {row.values[primary.key] ?? '—'}
                                </strong>
                            </div>
                        </div>
                        <LeaderboardMobileStats
                            columns={summaryColumns}
                            row={row}
                        />
                        {additionalColumns.length > 0 && (
                            <details className="leaderboard-entry-more">
                                <summary>
                                    {t(
                                        'leaderboard_ui.more_stats',
                                        'More stats'
                                    )}
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        aria-hidden="true"
                                        focusable="false">
                                        <path
                                            d="m4 6 4 4 4-4"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </summary>
                                <LeaderboardMobileStats
                                    columns={additionalColumns}
                                    row={row}
                                    className="leaderboard-entry-expanded-stats"
                                />
                            </details>
                        )}
                    </article>
                ))}
            </div>
        </>
    );
};

export const LeaderboardPagination = ({
    totalRecords,
    offset,
    perPage,
    onChange,
}: {
    totalRecords: number;
    offset: number;
    perPage: number;
    onChange: (offset: number) => void;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    return totalRecords > 0 ? (
        <div ref={ref} className="leaderboard-pagination">
            <CustomPagination
                totalRecords={totalRecords}
                offset={offset}
                recordsPerPage={perPage}
                handlePageChange={next => {
                    const card = ref.current?.closest('.dashboard-tabbed-card');
                    if (card && card.getBoundingClientRect().top < 0)
                        card.scrollIntoView({block: 'start'});
                    onChange(next);
                }}
            />
        </div>
    ) : null;
};
